import {
  AlertTriangle,
  BookOpen,
  Check,
  ChevronDown,
  Clock,
  Database,
  Dna,
  ExternalLink,
  FlaskConical,
  HelpCircle,
  LifeBuoy,
  MapPin,
  Microscope,
  Sparkles,
  SquarePen,
  TestTubes,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  SPECIES,
  type ConservationHit,
  type DecisionAction,
  type DecisionDto,
  type InteractionCandidate,
  type PartnerProteinDetail,
  type ProteinInfo,
  type SpeciesKey,
} from "@shared/schema";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { UncertaintyBadge } from "./common/UncertaintyBadge";
import { EVIDENCE_TIER_META } from "@/lib/evidence";
import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { cn, toDisplayScore } from "@/lib/utils";

interface Props {
  row: InteractionCandidate | null;
  proteinInfo: Record<string, ProteinInfo>;
  partnerDetails: Record<string, PartnerProteinDetail>;
  queryRunId?: string;
  existingDecision?: DecisionDto;
  onClose: () => void;
}

export function InteractionDetailDrawer({
  row,
  proteinInfo,
  partnerDetails,
  queryRunId,
  existingDecision,
  onClose,
}: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (row) {
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    }
  }, [row, onClose]);

  if (!row) return null;

  const tier = EVIDENCE_TIER_META[row.evidenceTier];
  const source = proteinInfo[row.sourceGene];
  const partner = proteinInfo[row.partnerGene];
  const partnerDetail = partnerDetails[row.partnerGene];

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-background/60 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close drawer"
      />
      <div className="relative z-10 flex h-full w-full max-w-[420px] flex-col border-l border-border bg-card shadow-xl">
        <DrawerHeader row={row} tier={tier} onClose={onClose} />
        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <WhyThisInteraction row={row} />
          <EvidenceChannelsSection row={row} />
          <MissingSection row={row} />
          <UpgradesSection row={row} />
          <LabPrioritySection row={row} />
          <AssaySection row={row} />
          <DecisionSection
            row={row}
            queryRunId={queryRunId}
            existingDecision={existingDecision}
          />
          <InteractionDetailsSection row={row} />
          <ConservationSection row={row} />
          <ExternalResources row={row} source={source} partner={partner} />
          {partnerDetail && (
            <PartnerProteinDetailCard detail={partnerDetail} alphaFoldAvg={row.alphaFold?.averagePlddt} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Header ───────────────────────── */

function DrawerHeader({
  row,
  tier,
  onClose,
}: {
  row: InteractionCandidate;
  tier: (typeof EVIDENCE_TIER_META)[keyof typeof EVIDENCE_TIER_META];
  onClose: () => void;
}) {
  return (
    <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-border bg-card/90 px-5 py-3 backdrop-blur">
      <div className="min-w-0">
        <div className="font-display text-base font-semibold tracking-tight">
          <span className="font-mono">{row.sourceGene}</span>
          <span className="mx-1 text-muted-foreground">—</span>
          <span className="font-mono">{row.partnerGene}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Badge color={tier.color}>{tier.label}</Badge>
          <UncertaintyBadge level={row.uncertainty} />
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
        <X className="h-4 w-4" />
      </Button>
    </header>
  );
}

/* ───────────────────────── Section scaffolding ───────────────────────── */

function Section({
  title,
  icon: Icon,
  children,
  variant,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  variant?: "warning";
}) {
  return (
    <section
      className={cn(
        "rounded-md border border-transparent",
        variant === "warning" && "border-[hsl(38_90%_60%/0.4)] bg-[hsl(44_94%_96%)]/40 p-3 dark:bg-[hsl(38_42%_16%)]/40",
      )}
    >
      <h3 className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </h3>
      {children}
    </section>
  );
}

/* ───────────────────────── 1. Why this interaction is here ───────────────────────── */

function WhyThisInteraction({ row }: { row: InteractionCandidate }) {
  const combinedDisplay = toDisplayScore(row.scores.combined);
  return (
    <Section title="Why is this interaction here?" icon={HelpCircle}>
      <p className="text-sm text-foreground">
        {row.explanation?.headline ??
          `${row.sourceGene}—${row.partnerGene}: inherits its evidence tier from STRING. Combined STRING score ${combinedDisplay}.`}
      </p>
      {row.explanation?.composition && (
        <div className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-[12px] leading-relaxed text-muted-foreground">
          {row.explanation.composition}
        </div>
      )}
      {row.explanation?.bullets && row.explanation.bullets.length > 0 && (
        <ul className="mt-2 space-y-1 text-[12px]">
          {row.explanation.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--priority-high))]" />
              <span className="text-foreground">{b}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/* ───────────────────────── 2. Evidence Channels ───────────────────────── */

function EvidenceChannelsSection({ row }: { row: InteractionCandidate }) {
  const CHANNELS: Array<{ key: keyof typeof row.scores; label: string; color: string }> = [
    { key: "experimental", label: "Experimental", color: "hsl(var(--channel-experimental))" },
    { key: "database", label: "Database", color: "hsl(var(--channel-database))" },
    { key: "textmining", label: "Text mining", color: "hsl(var(--channel-textmining))" },
    { key: "coexpression", label: "Co-expression", color: "hsl(var(--channel-coexpression))" },
    { key: "neighborhood", label: "Neighborhood", color: "hsl(var(--channel-neighborhood))" },
    { key: "fusion", label: "Fusion", color: "hsl(var(--channel-fusion))" },
    { key: "cooccurrence", label: "Co-occurrence", color: "hsl(var(--channel-cooccurrence))" },
  ];
  const active = CHANNELS.filter((c) => ((row.scores[c.key] as number | undefined) ?? 0) > 0.05).length;

  return (
    <Section title="Evidence Channels" icon={Database}>
      <p className="text-[12px] text-muted-foreground">
        These channels are <strong>not biologically equivalent</strong>. Experimental and database
        evidence reflect direct evidence; text mining and co-expression do not imply binding.
      </p>
      <ul className="mt-3 space-y-1.5">
        {CHANNELS.map((c) => {
          const v = (row.scores[c.key] as number | undefined) ?? 0;
          const disp = toDisplayScore(v);
          return (
            <li key={c.key} className="flex items-center gap-3 text-[12px]">
              <span className="w-24 shrink-0 text-muted-foreground">{c.label}</span>
              <span className="score-bar flex-1">
                <span
                  className="score-bar__fill"
                  style={{ width: `${(disp / 1000) * 100}%`, backgroundColor: c.color, opacity: v > 0 ? 1 : 0.2 }}
                />
              </span>
              <span className="w-10 text-right font-mono tabular-nums">{disp}</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>Combined: {toDisplayScore(row.scores.combined)}</span>
        <span>Active channels: {active}</span>
      </div>
    </Section>
  );
}

/* ───────────────────────── 3. What is missing ───────────────────────── */

function MissingSection({ row }: { row: InteractionCandidate }) {
  const missing = row.explanation?.missing ?? [];
  if (missing.length === 0) return null;
  return (
    <Section title="What is missing" icon={AlertTriangle}>
      <ul className="space-y-1 text-[12px]">
        {missing.map((m, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-0.5 inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-destructive/40 text-center leading-[10px] text-destructive">
              –
            </span>
            <span className="text-foreground">{m}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/* ───────────────────────── 4. What would upgrade confidence ───────────────────────── */

function UpgradesSection({ row }: { row: InteractionCandidate }) {
  const upgrades = row.explanation?.upgrades ?? [];
  if (upgrades.length === 0) return null;
  return (
    <Section title="What would upgrade confidence" icon={Sparkles}>
      <ul className="space-y-1 text-[12px]">
        {upgrades.map((u, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-0.5 inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-accent/60 text-center leading-[10px] text-accent">
              +
            </span>
            <span className="text-foreground">{u}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/* ───────────────────────── 5. Lab priority (big score + factor model) ───────────────────────── */

function LabPrioritySection({ row }: { row: InteractionCandidate }) {
  const [open, setOpen] = useState(false);
  if (!row.labPriority) return null;
  const score = Math.round(row.labPriority.score * 100);

  const FACTORS: Array<{ key: keyof typeof row.labPriority.factors; label: string }> = [
    { key: "experimental", label: "Experimental" },
    { key: "aiConfidence", label: "AI / comp" },
    { key: "diseaseRelevance", label: "Disease" },
    { key: "novelty", label: "Novelty" },
    { key: "tractability", label: "Tractability" },
    { key: "conservation", label: "Conservation" },
  ];

  return (
    <Section title="Lab Priority Score" icon={LifeBuoy}>
      <div className="flex items-baseline gap-3">
        <span
          className="font-display text-5xl font-bold leading-none"
          style={{
            color:
              score >= 60
                ? "hsl(var(--priority-high))"
                : score >= 40
                ? "hsl(var(--priority-mid))"
                : "hsl(var(--priority-low))",
          }}
        >
          {score}
        </span>
        <span className="text-sm text-muted-foreground">/100</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
        >
          <ChevronDown
            className={cn("h-3 w-3 transition-transform", open && "rotate-180")}
          />
          {open ? "Hide model" : "Show model"}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-2 rounded-md border border-border bg-muted/30 p-3 text-[11px]">
          <p className="text-muted-foreground">
            6-factor blend: experimental support, AI / computational confidence, disease relevance,
            novelty, tractability, and cross-species conservation. Weights are tuned per disease mode
            via the context KB.
          </p>
          <ul className="space-y-1.5">
            {FACTORS.map((f) => {
              const v = row.labPriority!.factors[f.key];
              return (
                <li key={f.key} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 text-muted-foreground">{f.label}</span>
                  <span className="score-bar flex-1">
                    <span className="score-bar__fill" style={{ width: `${v * 100}%` }} />
                  </span>
                  <span className="w-8 text-right font-mono tabular-nums">{Math.round(v * 100)}</span>
                </li>
              );
            })}
          </ul>
          {row.labPriority.rationale.length > 0 && (
            <div className="border-t border-border pt-2">
              <div className="mb-1 font-semibold uppercase tracking-wide text-muted-foreground">
                Why prioritised
              </div>
              <ul className="space-y-0.5">
                {row.labPriority.rationale.map((r, i) => (
                  <li key={i} className="text-foreground">· {r}</li>
                ))}
              </ul>
            </div>
          )}
          {row.labPriority.caveats.length > 0 && (
            <div className="border-t border-border pt-2 text-destructive">
              <div className="mb-1 font-semibold uppercase tracking-wide">Caveats</div>
              <ul className="space-y-0.5">
                {row.labPriority.caveats.map((c, i) => (
                  <li key={i}>· {c}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}

/* ───────────────────────── 6. Assay recommendations ───────────────────────── */

function AssaySection({ row }: { row: InteractionCandidate }) {
  const assay = row.explanation?.assay;
  const first =
    assay?.first ??
    row.labPriority?.suggestedPrimaryAssay ??
    "Co-IP with endogenous antibodies under native conditions; verify by western blot";
  const orthogonal =
    assay?.orthogonal ??
    row.labPriority?.suggestedOrthogonalAssay ??
    "Y2H or BiFC in an orthogonal cellular context; PLA for in situ confirmation";
  const controls =
    assay?.controls ??
    row.labPriority?.suggestedControls ??
    "IgG isotype control; single-protein IP; reciprocal co-IP; knockdown rescue";

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-[12px]">
      <div className="space-y-2">
        <AssayRow icon={FlaskConical} label="First assay" body={first} />
        <AssayRow icon={TestTubes} label="Orthogonal" body={orthogonal} />
        <AssayRow icon={SquarePen} label="Controls" body={controls} />
      </div>
    </div>
  );
}

function AssayRow({
  icon: Icon,
  label,
  body,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
      <p className="text-foreground">
        <strong className="font-semibold">{label}:</strong> {body}
      </p>
    </div>
  );
}

/* ───────────────────────── 7. Decision ───────────────────────── */

function DecisionSection({
  row,
  queryRunId,
  existingDecision,
}: {
  row: InteractionCandidate;
  queryRunId?: string;
  existingDecision?: DecisionDto;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState(existingDecision?.reason ?? "");

  const createMut = useMutation({
    mutationFn: (action: DecisionAction) =>
      api.createDecision({
        interactionId: row.id,
        sourceGene: row.sourceGene,
        partnerGene: row.partnerGene,
        speciesKey: row.speciesKey,
        action,
        reason: reason.trim() ? reason.trim() : null,
        queryRunId: queryRunId ?? null,
        deviceId: getDeviceId(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["decisions"] });
      qc.invalidateQueries({ queryKey: ["run-decisions", queryRunId] });
    },
  });

  const current = existingDecision?.action;

  const ACTIONS: Array<{
    key: DecisionAction;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
    { key: "validate_now", label: "Validate now", icon: Check },
    { key: "maybe_later", label: "Maybe later", icon: Clock },
    { key: "reject", label: "Reject", icon: X },
  ];

  return (
    <Section title="Decision" icon={BookOpen}>
      <div className="grid grid-cols-3 gap-2">
        {ACTIONS.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => createMut.mutate(a.key)}
            disabled={createMut.isPending}
            className={cn(
              "inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors",
              current === a.key
                ? "border-accent bg-accent/10 text-foreground"
                : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted",
            )}
          >
            <a.icon className="h-3 w-3" />
            {a.label}
          </button>
        ))}
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
        placeholder="Reason for decision (optional)..."
        rows={2}
        className="mt-2 flex w-full resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-[12px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {current && (
        <p className="mt-1.5 text-[10px] text-muted-foreground">
          Last saved: <span className="font-medium text-foreground">{current.replace("_", " ")}</span>
          {existingDecision?.createdAt &&
            ` · ${new Date(existingDecision.createdAt).toLocaleString()}`}
        </p>
      )}
    </Section>
  );
}

/* ───────────────────────── 8. Interaction details (kv table) ───────────────────────── */

function InteractionDetailsSection({ row }: { row: InteractionCandidate }) {
  const tier = EVIDENCE_TIER_META[row.evidenceTier];
  const isExperimental = (row.scores.experimental ?? 0) >= 0.15;
  const sources: string[] = [];
  if ((row.scores.experimental ?? 0) > 0) sources.push("STRING v12, Experimental evidence");
  if ((row.scores.database ?? 0) > 0) sources.push("Curated databases (BioGRID/IntAct)");
  if ((row.scores.textmining ?? 0) > 0) sources.push("Literature mining");

  const rows: Array<[string, React.ReactNode]> = [
    ["Type", "Physical"],
    ["Species", SPECIES[row.speciesKey].label],
    ["Exp. support", isExperimental ? "Yes" : "Indirect"],
    ["Sources", sources.length ? sources.join(" · ") : "—"],
    ["Evidence type", tier.label],
    ["Curation", tier.description],
  ];
  return (
    <Section title="Interaction Details" icon={Dna}>
      <dl className="grid grid-cols-[110px,1fr] gap-x-3 gap-y-1 text-[12px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="text-foreground">{v}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

/* ───────────────────────── 9. Species conservation ───────────────────────── */

function ConservationSection({ row }: { row: InteractionCandidate }) {
  const hits: ConservationHit[] = row.conservationHits ?? [];
  const ORDER: SpeciesKey[] = ["human", "mouse", "rat", "zebrafish", "drosophila"];
  const byKey = new Map(hits.map((h) => [h.species, h.evidence] as const));
  return (
    <Section title="Species Conservation" icon={MapPin}>
      <ul className="space-y-1 text-[12px]">
        {ORDER.map((k) => {
          const ev = byKey.get(k) ?? "none";
          const label = ev === "direct" ? "Direct" : ev === "inferred" ? "Inferred" : "—";
          return (
            <li key={k} className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-foreground">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{
                    backgroundColor:
                      ev === "direct"
                        ? "hsl(var(--priority-high))"
                        : ev === "inferred"
                        ? "hsl(var(--priority-high) / 0.45)"
                        : "hsl(var(--muted-foreground) / 0.3)",
                  }}
                />
                {SPECIES[k].label} ({SPECIES[k].common})
              </span>
              <span
                className={cn(
                  "text-xs",
                  ev === "direct" && "text-[hsl(var(--priority-high))]",
                  ev === "inferred" && "text-[hsl(var(--channel-database))]",
                  ev === "none" && "text-muted-foreground",
                )}
              >
                {label}
              </span>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

/* ───────────────────────── 10. External resources ───────────────────────── */

function ExternalResources({
  row,
  source,
  partner,
}: {
  row: InteractionCandidate;
  source?: ProteinInfo;
  partner?: ProteinInfo;
}) {
  const partnerId = partner?.uniprotId ?? row.partnerUniprotId;
  const sourceId = source?.uniprotId ?? row.sourceUniprotId;
  const stringUrl = `https://string-db.org/cgi/network?identifiers=${encodeURIComponent(
    `${row.sourceGene}%0D${row.partnerGene}`,
  )}`;
  return (
    <Section title="External Resources" icon={ExternalLink}>
      <ul className="space-y-1 text-[12px]">
        <ResourceLink href={stringUrl} label={`STRING network: ${row.sourceGene} + ${row.partnerGene}`} />
        {partnerId && (
          <ResourceLink
            href={`https://www.uniprot.org/uniprotkb/${partnerId}`}
            label={`UniProt: ${partnerId}`}
          />
        )}
        {sourceId && (
          <ResourceLink
            href={`https://alphafold.ebi.ac.uk/entry/${sourceId}`}
            label={`AlphaFold DB: ${row.sourceGene}`}
          />
        )}
        <ResourceLink
          href={`https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(
            `${row.sourceGene} ${row.partnerGene}`,
          )}`}
          label={`PubMed: ${row.sourceGene} + ${row.partnerGene}`}
        />
        <ResourceLink
          href={`https://thebiogrid.org/search.php?search=${encodeURIComponent(
            `${row.sourceGene} ${row.partnerGene}`,
          )}&organism=all&searchtype=all`}
          label={`IntAct: ${row.sourceGene} + ${row.partnerGene}`}
        />
        <ResourceLink
          href={`https://www.proteinatlas.org/search/${encodeURIComponent(row.partnerGene)}`}
          label={`HPA Tissue Atlas: ${row.partnerGene}`}
        />
      </ul>
    </Section>
  );
}

function ResourceLink({ href, label }: { href: string; label: string }) {
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-accent hover:underline"
      >
        <ExternalLink className="h-3 w-3" /> {label}
      </a>
    </li>
  );
}

/* ───────────────────────── 11. Partner Protein Detail (card with tabs) ───────────────────────── */

function PartnerProteinDetailCard({
  detail,
  alphaFoldAvg,
}: {
  detail: PartnerProteinDetail;
  alphaFoldAvg?: number;
}) {
  const [tab, setTab] = useState<"overview" | "structure" | "variants" | "binding">("overview");
  const tabsMeta = useMemo(
    () => [
      { id: "overview" as const, label: "Overview", count: undefined },
      { id: "structure" as const, label: "Structure", count: undefined },
      { id: "variants" as const, label: "Variants", count: undefined },
      {
        id: "binding" as const,
        label: "Binding sites",
        count: detail.bindingSites?.length || undefined,
      },
    ],
    [detail.bindingSites?.length],
  );

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <header className="mb-3 flex items-start justify-between gap-2">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Microscope className="h-3 w-3 text-accent" />
            Partner protein detail
          </div>
          <div className="mt-1 flex items-center gap-2 font-display text-sm font-semibold">
            {detail.proteinName ?? detail.geneName}{" "}
            <span className="font-mono text-xs text-muted-foreground">{detail.uniprotId}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            {detail.sequenceLength ? `${detail.sequenceLength} aa` : null}
            {detail.sequenceLength && detail.organism ? " · " : null}
            {detail.organism ? <em>{detail.organism}</em> : null}
          </div>
        </div>
      </header>

      <nav className="mb-3 flex items-center gap-1 border-b border-border text-[11px]">
        {tabsMeta.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "inline-flex items-center gap-1 border-b-2 px-2 py-1.5 transition-colors",
              tab === t.id
                ? "border-accent text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className="inline-flex h-4 items-center rounded-full bg-accent/20 px-1.5 text-[10px] font-semibold text-accent-foreground/80">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </nav>

      {tab === "overview" && <OverviewTab detail={detail} />}
      {tab === "structure" && <StructureTab detail={detail} alphaFoldAvg={alphaFoldAvg} />}
      {tab === "variants" && <VariantsTab detail={detail} />}
      {tab === "binding" && <BindingSitesTab detail={detail} />}
    </div>
  );
}

function OverviewTab({ detail }: { detail: PartnerProteinDetail }) {
  return (
    <div className="space-y-3 text-[12px]">
      <div className="flex flex-wrap gap-1.5">
        <ExtChip label="UniProt" href={`https://www.uniprot.org/uniprotkb/${detail.uniprotId}`} />
        <ExtChip
          label="RCSB PDB"
          href={`https://www.rcsb.org/search?request={"query":{"type":"terminal","service":"text","parameters":{"attribute":"entity_src_gen.pdbx_gene_src_scientific_name","operator":"contains_words","value":"${encodeURIComponent(
            detail.geneName,
          )}"}}}`}
        />
        <ExtChip
          label="ClinVar"
          href={`https://www.ncbi.nlm.nih.gov/clinvar/?term=${encodeURIComponent(detail.geneName)}`}
        />
        <ExtChip
          label="STRING"
          href={`https://string-db.org/cgi/network?identifiers=${encodeURIComponent(detail.geneName)}`}
        />
      </div>

      {detail.function && (
        <div>
          <h4 className="label-eyebrow mb-1">Function</h4>
          <p className="leading-relaxed text-foreground">{detail.function}</p>
        </div>
      )}

      {detail.subcellularLocations.length > 0 && (
        <div>
          <h4 className="label-eyebrow mb-1">Subcellular location</h4>
          <div className="flex flex-wrap gap-1.5">
            {detail.subcellularLocations.map((loc) => (
              <span key={loc} className="chip text-[11px]">
                {loc}
              </span>
            ))}
          </div>
        </div>
      )}

      {detail.molecularFunctionGo.length > 0 && (
        <div>
          <h4 className="label-eyebrow mb-1">Molecular function (GO)</h4>
          <div className="flex flex-wrap gap-1.5">
            {detail.molecularFunctionGo.slice(0, 12).map((g) => (
              <span
                key={g.id}
                className="inline-flex items-center gap-1 rounded-md border border-accent/30 bg-accent/5 px-1.5 py-0.5 text-[11px]"
              >
                <span className="text-[9px] font-semibold uppercase text-accent">{g.aspect}</span>
                {g.label}
              </span>
            ))}
          </div>
        </div>
      )}
      {detail.biologicalProcessGo.length > 0 && (
        <div>
          <h4 className="label-eyebrow mb-1">Biological process (GO)</h4>
          <div className="flex flex-wrap gap-1.5">
            {detail.biologicalProcessGo.slice(0, 12).map((g) => (
              <span
                key={g.id}
                className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--channel-database))]/30 bg-[hsl(var(--channel-database))]/10 px-1.5 py-0.5 text-[11px]"
              >
                <span className="text-[9px] font-semibold uppercase text-[hsl(var(--channel-database))]">
                  {g.aspect}
                </span>
                {g.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StructureTab({
  detail,
  alphaFoldAvg,
}: {
  detail: PartnerProteinDetail;
  alphaFoldAvg?: number;
}) {
  const avg = detail.averagePlddt ?? alphaFoldAvg;
  return (
    <div className="space-y-2 text-[12px]">
      <h4 className="label-eyebrow">Structure confidence (pLDDT)</h4>
      {avg != null ? (
        <div className="flex items-center gap-3">
          <span
            className="font-display text-3xl font-bold"
            style={{
              color:
                avg >= 70
                  ? "hsl(var(--priority-high))"
                  : avg >= 50
                  ? "hsl(var(--priority-mid))"
                  : "hsl(var(--priority-low))",
            }}
          >
            {avg.toFixed(1)}
          </span>
          <a
            href={`https://alphafold.ebi.ac.uk/entry/${detail.uniprotId}`}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-accent hover:underline"
          >
            View on AlphaFold DB →
          </a>
        </div>
      ) : (
        <p className="text-muted-foreground">No AlphaFold model available for this protein.</p>
      )}
      <p className="text-[11px] text-muted-foreground">
        pLDDT is a per-residue confidence metric; the displayed value is a chain-level average.
        AlphaFold predictions are structural hypotheses — orthogonal experimental confirmation is
        required before biological interpretation.
      </p>
    </div>
  );
}

function VariantsTab({ detail }: { detail: PartnerProteinDetail }) {
  return (
    <div className="space-y-2 text-[12px] text-muted-foreground">
      <p>
        Variant integration (ClinVar, gnomAD, missense constraint) is scheduled for Stage 1 so that
        annotations ship alongside workspace / team features. For now, use the external links below
        for manual inspection.
      </p>
      <div className="flex flex-wrap gap-1.5">
        <ExtChip
          label="ClinVar"
          href={`https://www.ncbi.nlm.nih.gov/clinvar/?term=${encodeURIComponent(detail.geneName)}`}
        />
        <ExtChip
          label="gnomAD"
          href={`https://gnomad.broadinstitute.org/gene/${encodeURIComponent(detail.geneName)}`}
        />
      </div>
    </div>
  );
}

function BindingSitesTab({ detail }: { detail: PartnerProteinDetail }) {
  const grouped = groupByKind(detail.bindingSites ?? []);
  const siteTotal = detail.bindingSites?.length ?? 0;
  return (
    <div className="space-y-2 text-[12px]">
      <p className="text-muted-foreground">
        Functionally annotated residues from UniProt (active sites, binding sites, metal binding).
        These are experimentally supported annotations — not computational pocket predictions.
      </p>
      {siteTotal === 0 ? (
        <p className="rounded-md border border-dashed border-border px-3 py-3 text-muted-foreground">
          No curated binding-site or active-site annotations available on UniProt for this protein.
        </p>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped).map(([kind, sites]) => (
            <div key={kind} className="rounded-md border border-border bg-muted/20 p-2">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">{humanizeKind(kind)}</span>
                {sites.length > 1 && (
                  <span className="rounded-full bg-[hsl(var(--priority-mid))]/20 px-2 py-0.5 text-[10px] font-semibold text-[hsl(26_80%_36%)]">
                    Multiple sites
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {sites.flatMap((s) =>
                  s.residues.map((r, i) => (
                    <span
                      key={`${kind}-${i}-${r}`}
                      className="inline-flex items-center justify-center rounded-md border border-accent/40 bg-accent/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-accent"
                    >
                      {r}
                    </span>
                  )),
                )}
              </div>
              {sites[0]?.description && (
                <p className="mt-1 text-[11px] text-muted-foreground">{sites[0].description}</p>
              )}
              {sites[0]?.source && (
                <p className="mt-0.5 text-[10px] italic text-muted-foreground">
                  Source: {sites[0].source}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="rounded-md border border-border bg-muted/30 p-2 text-[11px] text-muted-foreground">
        <strong>Scope:</strong> This panel reports existing UniProt/Swiss-Prot curated annotations only.
        No computational pocket prediction is performed. For in-silico binding-site prediction, consider
        FPocket, P2Rank, or SiteMap on the AlphaFold model.
      </div>
    </div>
  );
}

function ExtChip({ label, href }: { label: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px] text-foreground hover:bg-muted"
    >
      {label}
      <ExternalLink className="h-2.5 w-2.5" />
    </a>
  );
}

function groupByKind<T extends { kind: string }>(items: T[]): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  for (const it of items) {
    (out[it.kind] ??= []).push(it);
  }
  return out;
}

function humanizeKind(kind: string): string {
  switch (kind) {
    case "binding_site":
      return "Binding site";
    case "active_site":
      return "Active site";
    case "metal_binding":
      return "Metal binding";
    case "site":
      return "Site";
    default:
      return kind;
  }
}
